import { TextAreaControl, TextFieldControl, ToggleBoxControl } from '@anecdotes/design-system/form-controls';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { DynamicFormGroup } from '@anecdotes/dynamic-form';
import { RequirementResponse, RequirementUpdateRequest } from '@anecdotes/shared-trust-center';
import { RequirementNavigationService } from '@anecdotes/data-store';
import { TranslateService } from '@ngx-translate/core';

export enum FormControls {
  Name = 'name',
  Description = 'description',
  IsVisible = 'isVisible',
}

@Component({
  selector: 'app-requirement-edit',
  templateUrl: './requirement-edit.component.html',
  styleUrls: ['./requirement-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RequirementEditComponent implements OnInit {
  private initialFormValues: {
    [FormControls.Name]: string;
    [FormControls.IsVisible]: boolean;
    [FormControls.Description]: string;
  };

  @HostBinding('class')
  private readonly classes = 'h-full';

  @Input()
  requirement: RequirementResponse;

  @Input()
  displayLinkedFrameworks: boolean;

  @Input()
  showMonitoredIndication: boolean;

  form: DynamicFormGroup<any>;

  readonly controlLabelMapping: Record<string, string> = {
    '=1': this.buildTranslationKey('controlSummary.control'),
    other: this.buildTranslationKey('controlSummary.controls'),
  };

  get formValue(): RequirementUpdateRequest {
    return {
      id: this.requirement.id,
      name: this.form.value[FormControls.Name],
      description: this.form.value[FormControls.Description],
      is_visible: this.form.value[FormControls.IsVisible],
    };
  }

  get wasFormValueChanged(): boolean {
    if (!this.form) {
      return false;
    }

    return Object.values(FormControls).some((name) => this.form.items[name].value !== this.initialFormValues[name]);
  }

  get isInvisibleSelected(): boolean {
    return this.form?.controls['isVisible'].value === false;
  }

  get frameworks(): string[] {
    return Object.keys(this.requirement.linked_frameworks);
  }

  get controls(): string[] {
    return Object.values(this.requirement.linked_frameworks).flat();
  }

  get linkedFrameworksTranslationKey(): string {
    switch (true) {
      case this.controls.length === 1 && this.frameworks.length === 1:
        return this.buildTranslationKey('linkedFrameworks.singleControlSingleFramework');
      case this.controls.length === 1 && this.frameworks.length !== 1:
        return this.buildTranslationKey('linkedFrameworks.singleControlPluralFrameworks');
      case this.controls.length !== 1 && this.frameworks.length === 1:
        return this.buildTranslationKey('linkedFrameworks.pluralControlsSingleFramework');
      case this.controls.length !== 1 && this.frameworks.length !== 1:
        return this.buildTranslationKey('linkedFrameworks.pluralControlsPluralFrameworks');
    }
  }

  constructor(
    private requirementNavigationService: RequirementNavigationService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  buildTranslationKey(relativeKey: string): string {
    return `trustCenter.${relativeKey}`;
  }

  resetRequirementName(): void {
    this.form.controls[FormControls.Name].setValue(this.requirement.original_name);
  }

  navigateToRequirement(): void {
    this.requirementNavigationService.navigateToRequirementById(this.requirement.id);
  }

  getFrameworkControls(framework: string): string[] {
    return this.requirement.linked_frameworks[framework];
  }

  private initForm(): void {
    this.form = new DynamicFormGroup({
      [FormControls.IsVisible]: new ToggleBoxControl({
        initialInputs: {
          title: this.translate.instant(this.buildTranslationKey('show')),
        },
        initialValue: this.requirement.is_visible,
      }),
      [FormControls.Name]: new TextFieldControl({
        initialInputs: {
          validateOnDirty: true,
          required: true,
          label: this.buildTranslationKey('requirementName'),
          displayCharactersCounter: true,
          maxLength: 70,
        },
        initialValue: this.requirement.name,
        validators: [Validators.required, Validators.maxLength(70)],
      }),
      [FormControls.Description]: new TextAreaControl({
        initialInputs: {
          validateOnDirty: true,
          required: true,
          label: this.buildTranslationKey('description'),
          rows: 3,
          resizable: false,
        },
        validators: [Validators.required],
        initialValue: this.requirement.description,
      }),
    });

    this.initialFormValues = this.form.value;
  }
}
